import { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import React, { FC, useRef } from 'react'

import { NextSeo, CorporateContactJsonLd } from 'next-seo'

import { Box, Container, Grid } from '@material-ui/core'

import AppLayout from '~/components/layouts/AppLayout'
import Breadcrumbs from '~/components/shared/Breadcrumbs'
import HeroSection from '~/components/shared/HeroSection'
import Loader from '~/components/shared/Loader'
import { DealerTitle } from '~/views/Dealer/DealerProfile/DealerTitle'
import DealerDetailsSection from '~/views/Dealer/DealerProfile/DetailSection'
import { DealerItemsSection } from '~/views/Dealer/DealerProfile/ItemsSection'
import useDealerInfo from '~/views/Dealer/DealerProfile/useDealerInfo'
import useViewContactMeta from '~/views/Dealer/DealerProfile/useViewContactMeta'

import { useMobile } from '~/hooks/responsive'
import Deserializer from '~/services/Deserializer'
import EmpriseGroup from '~/services/EmpriseGroup'
import Paths from '~/utils/paths'

import { formatMessage, getEntityByBrand } from '~/brands'
import { Dealer } from '~/models/Dealer'

import bgImageBoat from '~/../public/static/images/vendor-page.boat.jpg'
import bgImageRv from '~/../public/static/images/vendor-page.rv.jpg'

const bgImage = getEntityByBrand<string>({ rv: bgImageRv, boat: bgImageBoat })

type Props = {
  data: Dealer
}

const DealerPage: FC<Props> = ({ data }) => {
  const router = useRouter()
  const ref = useRef()
  const isMobile = useMobile()

  if (router.isFallback) {
    return <Loader />
  }

  useViewContactMeta(data)

  const {
    data: { dealer, listings = [], pagy = {} } = {},
    isLoading,
    error
  } = useDealerInfo(data, router)

  const loading = isLoading && !listings.length && !error

  const breadcrumbs = [
    {
      href: Paths.dealer.index,
      child: 'Dealers'
    },
    {
      href: '/',
      child: data.name
    }
  ]

  const phone = data.phone?.replace(' ', 'g') || ''

  return (
    <AppLayout bgcolor="bg.primary.main">
      <NextSeo
        title={data.name}
        description={formatMessage('dealerPage.meta.description')}
        canonical={Paths.dealer.buildDealer({ slug: String(data.slug) })}
        openGraph={{
          title: data.name,
          description: formatMessage('dealerPage.meta.description')
        }}
      />

      <CorporateContactJsonLd
        url={Paths.dealer.buildDealer({ slug: data.slug })}
        logo={data?.logo?.url}
        contactPoint={[
          {
            contactType: 'customer service',
            telephone: phone
              ? phone.replace(phone.substring(0, 6), 'XXXXXX')
              : 'N/A',
            areaServed: 'AU',
            availableLanguage: ['English']
          }
        ]}
      />

      <Box>
        <HeroSection src={bgImage}>
          <Container maxWidth="xl" style={{ height: '350px' }}>
            <Breadcrumbs color="light" items={breadcrumbs} />
            <DealerTitle data={data} />
          </Container>
        </HeroSection>
        <Container maxWidth="xl">
          <Box mt={-5} position="relative" zIndex={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DealerDetailsSection data={data} dealer={dealer} />
              </Grid>
              <Grid item xs={12} ref={ref}>
                <DealerItemsSection
                  listings={listings}
                  loading={loading}
                  data={data}
                  pagy={pagy}
                  isMobile={isMobile}
                  isValidating={isLoading}
                  router={router}
                />
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </AppLayout>
  )
}

export async function getStaticPaths() {
  const { slugs } = await EmpriseGroup.getDealerSlugs()

  const paths = slugs.map((slug) => ({ params: { slug } }))

  return {
    paths,
    fallback: true
  }
}

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const slug = context.params?.slug

    if (!slug) {
      throw new Error('Slug not found')
    }

    const resp = await EmpriseGroup.getDealer(slug as string)
    const data = await Deserializer.deserialize(resp?.dealer)

    if (!data || !resp) {
      return { notFound: true }
    }

    return { props: { data } }
  } catch (e) {
    return { notFound: true }
  }
}

export default DealerPage
